// A manual check: the guide of a criterion (what to check, how, what should happen, how to record it), the form
// to record the review (page, element, device, conditions, answer, evidence), the drafts to resume and every
// earlier review with the instructions it followed. Saving says it was done, never that it was passed: only
// the answer says that, «No he podido comprobarlo» and no answer are never favourable, and a registered review
// is never changed (repeating it adds a new one).
import { ArrowLeftIcon, CheckCircledIcon } from '@radix-ui/react-icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams, useSearch } from '@tanstack/react-router';
import { type FormEvent, useEffect, useRef, useState } from 'react';
import { Form } from 'react-aria-components';
import { useTranslation } from 'react-i18next';
import {
  ApiError,
  CONDITIONS,
  type Condition,
  discardDraft,
  type GuideDetail,
  manualGuideQuery,
  meQuery,
  REVIEW_ANSWERS,
  type ReviewAnswer,
  type ReviewDevice,
  type ReviewPatch,
  type ReviewSeverity,
  type ReviewView,
  registerReview,
  saveReview,
  scansQuery,
  startReview,
} from '../api';
import { writeStored } from '../persist';
import {
  announce,
  Button,
  buttonClass,
  ConfirmButton,
  Field,
  Message,
  useArrivalFocus,
  usePageTitle,
} from '../ui';
import { pathOf } from '../util';
import { rightsOf } from './follow-state';
import { HelpBadge } from './manual-parts';
import { useDate } from './scan-parts';

const SELECT_CLASS =
  'min-h-11 rounded-md border border-field-border bg-paper px-3 py-2 text-ink data-[invalid]:border-error';

const errorCode = (failure: unknown) => (failure instanceof ApiError ? failure.code : null);

/** Everything that shows the manual checks reads again after a change. */
function useRefresh(siteId: string) {
  const queryClient = useQueryClient();
  return () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ['sites', siteId, 'manual'] }),
      // The results of every analysis of the website: the criteria, the progress and the problems found.
      queryClient.invalidateQueries({ queryKey: ['scans'] }),
      queryClient.invalidateQueries({ queryKey: scansQuery(siteId).queryKey }),
      queryClient.invalidateQueries({ queryKey: ['sites', siteId, 'follow-up'] }),
      queryClient.invalidateQueries({ queryKey: ['overview'] }),
    ]);
}

/** The guide: what to check, how, what should happen and how to record it, in this order. */
function Guide({ detail }: { detail: GuideDetail }) {
  const { t } = useTranslation();
  const guide = detail.guide;
  if (!guide) return <Message tone="error">{t('manual.noGuide')}</Message>;
  return (
    <div className="manual-guide">
      {guide.help === 'especializada' && guide.specialistNote && (
        <div className="manual-specialist">
          <p className="font-bold">{t('manual.guide.specialist')}</p>
          <p>{guide.specialistNote}</p>
        </div>
      )}
      <section aria-labelledby="guia-que">
        <h3 id="guia-que">{t('manual.guide.what')}</h3>
        <p>{guide.what}</p>
      </section>
      <section aria-labelledby="guia-como">
        <h3 id="guia-como">{t('manual.guide.how')}</h3>
        <ol className="guide-steps">
          {guide.how.map((step) => (
            <li key={step}>{step}</li>
          ))}
        </ol>
      </section>
      <section aria-labelledby="guia-deberia">
        <h3 id="guia-deberia">{t('manual.guide.expected')}</h3>
        <ul className="manual-list-plain">
          {guide.expected.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      </section>
      <section aria-labelledby="guia-registrar">
        <h3 id="guia-registrar">{t('manual.guide.record')}</h3>
        <ul className="manual-list-plain">
          {guide.record.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
        <p className="font-bold">{t('manual.guide.recordAnswers')}</p>
        <dl className="manual-answers">
          {REVIEW_ANSWERS.map((answer) => (
            <div key={answer}>
              <dt>{t(`manual.answer.${answer}`)}</dt>
              <dd>{t(`manual.answerHint.${answer}`)}</dd>
            </div>
          ))}
        </dl>
      </section>
    </div>
  );
}

/** One recorded review, with what was found, the evidence and the instructions that were followed. */
function ReviewCard({
  review,
  mayTrack,
  onRepeat,
  busy,
}: {
  review: ReviewView;
  mayTrack: boolean;
  onRepeat: (review: ReviewView) => void;
  busy: boolean;
}) {
  const { t } = useTranslation();
  const date = useDate();
  const none = t('manual.history.none');
  const text = (label: string, value: string | null) =>
    value ? (
      <div>
        <dt>{label}</dt>
        <dd className="wrap-anywhere whitespace-pre-line">{value}</dd>
      </div>
    ) : null;
  return (
    <li className="manual-review-card" data-current={review.current || undefined}>
      <p className="problem-chips">
        <span className="origin-chip">{review.answer ? t(`manual.answer.${review.answer}`) : '—'}</span>
        <span className="origin-chip">
          {review.current ? t('manual.history.current') : t('manual.history.earlier')}
        </span>
        {review.needsNew && <span className="origin-chip manual-needs-new">{t('manual.needsNew')}</span>}
        {review.occurrenceId && <span className="origin-chip">{t('manual.history.afterFix')}</span>}
      </p>
      <p>
        {t('manual.history.by', {
          name: review.by?.name || t('manual.row.someone'),
          date: date(review.registeredAt ?? review.updatedAt, 'long'),
        })}
      </p>
      {review.needsNew && <p className="text-sm">{t('manual.needsNewHint')}</p>}
      <dl className="manual-facts">
        <div>
          <dt>{t('manual.history.page')}</dt>
          <dd className="wrap-anywhere">
            {review.page ? (
              <>
                {review.page.title || pathOf(review.page.url)}{' '}
                <span className="text-ink-muted">({pathOf(review.page.url)})</span>
              </>
            ) : (
              t('manual.form.whole')
            )}
          </dd>
        </div>
        <div>
          <dt>{t('manual.history.element')}</dt>
          <dd>{review.element || none}</dd>
        </div>
        <div>
          <dt>{t('manual.history.device')}</dt>
          <dd>{t(`manual.form.devices.${review.device}`)}</dd>
        </div>
        <div>
          <dt>{t('manual.history.conditions')}</dt>
          <dd>
            {review.conditions.ids?.length || review.conditions.notes
              ? [
                  ...(review.conditions.ids ?? []).map((id) => t(`manual.condition.${id}`)),
                  ...(review.conditions.notes ? [review.conditions.notes] : []),
                ].join(' · ')
              : none}
          </dd>
        </div>
        {text(
          review.answer === 'no-comprobado' ? t('manual.history.reason') : t('manual.history.description'),
          review.description,
        )}
        {text(t('manual.history.justification'), review.justification)}
        {review.severity && review.answer === 'problema' && (
          <div>
            <dt>{t('manual.history.severity')}</dt>
            <dd>{t(`manual.form.severities.${review.severity}`)}</dd>
          </div>
        )}
        {text(t('manual.history.evidence'), review.evidence)}
      </dl>
      <details className="issue-details">
        <summary className="cursor-pointer font-bold">
          {t('manual.history.instructions', { version: review.instructions.version })}
        </summary>
        <div className="manual-guide">
          <p>{review.instructions.what}</p>
          <ol className="guide-steps">
            {review.instructions.how.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ol>
          <ul className="manual-list-plain">
            {review.instructions.expected.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </div>
      </details>
      {mayTrack && review.current && (
        <div className="flex flex-wrap items-center gap-3">
          <Button variant="secondary" isPending={busy} onPress={() => onRepeat(review)}>
            {t('manual.actions.repeat')}
          </Button>
        </div>
      )}
    </li>
  );
}

/** The form of a draft: what is being reviewed, under which conditions, the answer and its evidence. */
function ReviewEditor({
  draft,
  detail,
  siteId,
  onRegistered,
  onDiscarded,
}: {
  draft: ReviewView;
  detail: GuideDetail;
  siteId: string;
  onRegistered: () => void;
  onDiscarded: () => void;
}) {
  const { t } = useTranslation();
  const refresh = useRefresh(siteId);
  const scope = draft.instructions.scope;
  const [pageKey, setPageKey] = useState(draft.page?.key ?? '');
  const [element, setElement] = useState(draft.element ?? '');
  const [device, setDevice] = useState<ReviewDevice>(draft.device);
  const [conditions, setConditions] = useState<Condition[]>(draft.conditions.ids ?? []);
  const [notes, setNotes] = useState(draft.conditions.notes ?? '');
  const [answer, setAnswer] = useState<ReviewAnswer | ''>(draft.answer ?? '');
  const [description, setDescription] = useState(draft.description ?? '');
  const [justification, setJustification] = useState(draft.justification ?? '');
  const [evidence, setEvidence] = useState(draft.evidence ?? '');
  const [severity, setSeverity] = useState<ReviewSeverity>(draft.severity ?? detail.suggestedSeverity);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<'draft' | null>(null);
  const [busy, setBusy] = useState<'save' | 'register' | null>(null);
  const alert = useRef<HTMLDivElement>(null);

  const patch = (): ReviewPatch => ({
    answer: answer || null,
    ...(scope === 'page' ? { pageKey: pageKey || null } : {}),
    element: element.trim() || null,
    device,
    conditions: { ids: conditions, ...(notes.trim() ? { notes: notes.trim() } : {}) },
    description: description.trim() || null,
    justification: justification.trim() || null,
    evidence: evidence.trim() || null,
    severity: answer === 'problema' ? severity : null,
  });

  function fail(failure: unknown) {
    const code = errorCode(failure);
    setError(
      code &&
        [
          'respuesta',
          'pagina',
          'descripcion',
          'gravedad',
          'justificacion',
          'registrada',
          'permiso',
          'datos',
        ].includes(code)
        ? t(`manual.errors.${code}`)
        : t('manual.errors.generic'),
    );
    requestAnimationFrame(() => alert.current?.scrollIntoView({ block: 'nearest' }));
  }

  async function save() {
    setError(null);
    setSaved(null);
    setBusy('save');
    try {
      await saveReview(draft.id, patch());
      await refresh();
      setSaved('draft');
      announce(t('manual.saved.draft'));
    } catch (failure) {
      fail(failure);
    } finally {
      setBusy(null);
    }
  }

  async function register(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setSaved(null);
    setBusy('register');
    try {
      await registerReview(draft.id, patch());
      await refresh();
      announce(t('manual.saved.registered'));
      onRegistered();
    } catch (failure) {
      fail(failure);
    } finally {
      setBusy(null);
    }
  }

  const toggle = (condition: Condition, on: boolean) =>
    setConditions((list) => (on ? [...list, condition] : list.filter((item) => item !== condition)));

  return (
    <Form
      onSubmit={register}
      validationBehavior="native"
      className="manual-form"
      aria-labelledby="tu-revision"
    >
      <h3 id="tu-revision">{t('manual.form.title')}</h3>
      <div ref={alert}>{error && <Message tone="error">{error}</Message>}</div>

      <fieldset className="manual-fieldset">
        <legend className="font-bold">{t('manual.form.identify')}</legend>
        {scope === 'page' ? (
          <div className="flex flex-col gap-2">
            <label htmlFor="rev-pagina" className="font-bold">
              {t('manual.form.page')}
            </label>
            <p className="text-sm text-ink-muted" id="rev-pagina-ayuda">
              {t('manual.form.pageHelp')}
            </p>
            <select
              id="rev-pagina"
              className={SELECT_CLASS}
              value={pageKey}
              onChange={(event) => setPageKey(event.target.value)}
              aria-describedby="rev-pagina-ayuda"
            >
              <option value="">{t('manual.form.pagePlaceholder')}</option>
              {detail.pages.map((page) => (
                <option key={page.key} value={page.key}>
                  {page.title ? `${page.title} (${pathOf(page.url)})` : pathOf(page.url)}
                </option>
              ))}
            </select>
          </div>
        ) : (
          <p>{t('manual.page.siteScope')}</p>
        )}
        <Field
          name="element"
          label={t('manual.form.element')}
          description={t('manual.form.elementHelp')}
          value={element}
          onChange={setElement}
          maxLength={200}
        />
        <div className="flex flex-col gap-2" role="radiogroup" aria-labelledby="rev-dispositivo">
          <span id="rev-dispositivo" className="font-bold">
            {t('manual.form.device')}
          </span>
          {(['ambos', 'escritorio', 'movil'] as const).map((value) => (
            <label key={value} className="follow-check">
              <input
                type="radio"
                name="rev-dispositivo"
                value={value}
                checked={device === value}
                onChange={() => setDevice(value)}
              />
              {t(`manual.form.devices.${value}`)}
            </label>
          ))}
        </div>
        <fieldset className="manual-fieldset">
          <legend className="font-bold">{t('manual.form.conditions')}</legend>
          <p className="text-sm text-ink-muted">{t('manual.form.conditionsHelp')}</p>
          {CONDITIONS.map((condition) => (
            <label key={condition} className="follow-check">
              <input
                type="checkbox"
                checked={conditions.includes(condition)}
                onChange={(event) => toggle(condition, event.target.checked)}
              />
              {t(`manual.condition.${condition}`)}
            </label>
          ))}
          <Field
            name="notes"
            label={t('manual.form.conditionNotes')}
            description={t('manual.form.conditionNotesHelp')}
            value={notes}
            onChange={setNotes}
            maxLength={500}
          />
        </fieldset>
      </fieldset>

      <fieldset className="manual-fieldset">
        <legend className="font-bold">{t('manual.form.answer')}</legend>
        <p className="text-sm text-ink-muted">{t('manual.form.answerHelp')}</p>
        {REVIEW_ANSWERS.map((value) => (
          <label key={value} className="manual-answer-option" data-selected={answer === value || undefined}>
            <input
              type="radio"
              name="rev-respuesta"
              value={value}
              checked={answer === value}
              onChange={() => setAnswer(value)}
            />
            <span>
              <span className="font-bold">{t(`manual.answer.${value}`)}</span>
              <span className="block text-sm text-ink-muted">{t(`manual.answerHint.${value}`)}</span>
            </span>
          </label>
        ))}
        {answer === 'problema' && (
          <>
            <Field
              name="description"
              multiline
              label={t('manual.form.description')}
              description={t('manual.form.descriptionHelp')}
              value={description}
              onChange={setDescription}
              minLength={10}
              maxLength={2000}
              isRequired
            />
            <div className="flex flex-col gap-2" role="radiogroup" aria-labelledby="rev-gravedad">
              <span id="rev-gravedad" className="font-bold">
                {t('manual.form.severity')}
              </span>
              <span className="text-sm text-ink-muted">{t('manual.form.severityHelp')}</span>
              {(['grave', 'moderada', 'leve'] as const).map((value) => (
                <label key={value} className="follow-check">
                  <input
                    type="radio"
                    name="rev-gravedad"
                    value={value}
                    checked={severity === value}
                    onChange={() => setSeverity(value)}
                  />
                  {t(`manual.form.severities.${value}`)}
                </label>
              ))}
            </div>
          </>
        )}
        {answer === 'no-comprobado' && (
          <Field
            name="description"
            multiline
            label={t('manual.form.reason')}
            description={t('manual.form.reasonHelp')}
            value={description}
            onChange={setDescription}
            maxLength={2000}
          />
        )}
        {answer === 'no-aplica' && (
          <Field
            name="justification"
            multiline
            label={t('manual.form.justification')}
            description={t('manual.form.justificationHelp')}
            value={justification}
            onChange={setJustification}
            minLength={20}
            maxLength={2000}
            isRequired
          />
        )}
        <Field
          name="evidence"
          multiline
          label={t('manual.form.evidence')}
          description={t('manual.form.evidenceHelp')}
          value={evidence}
          onChange={setEvidence}
          maxLength={2000}
        />
      </fieldset>

      <div className="flex flex-wrap items-center gap-4">
        <Button
          type="button"
          variant="secondary"
          isPending={busy === 'save'}
          isDisabled={busy !== null}
          onPress={() => void save()}
        >
          {busy === 'save' ? t('manual.form.saving') : t('manual.form.save')}
        </Button>
        <Button type="submit" isPending={busy === 'register'} isDisabled={busy !== null}>
          {busy === 'register' ? t('manual.form.registering') : t('manual.form.register')}
        </Button>
        <ConfirmButton
          label={t('manual.form.discard')}
          title={t('manual.form.discardConfirm')}
          body={t('manual.form.discardBody')}
          confirm={t('manual.form.discardConfirm')}
          cancel={t('manual.form.cancel')}
          onConfirm={async () => {
            try {
              await discardDraft(draft.id);
              await refresh();
              announce(t('manual.saved.discarded'));
              onDiscarded();
            } catch (failure) {
              fail(failure);
            }
          }}
        />
      </div>
      <div role="status" className="follow-saved">
        {saved === 'draft' && (
          <p>
            <CheckCircledIcon aria-hidden="true" /> {t('manual.saved.draft')}
          </p>
        )}
      </div>
    </Form>
  );
}

/** The page of a manual check. */
export function ManualReviewPage() {
  const { t } = useTranslation();
  const date = useDate();
  const { siteId, criterion } = useParams({ from: '/sites/$siteId/revision-manual/$criterion' });
  const search = useSearch({ from: '/sites/$siteId/revision-manual/$criterion' });
  const { data: me } = useQuery(meQuery);
  const rights = rightsOf(me);
  const detail = useQuery({ ...manualGuideQuery(siteId, criterion), retry: false });
  const refresh = useRefresh(siteId);
  const [activeId, setActiveId] = useState<string | null>(search.r ?? null);
  const [recorded, setRecorded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);
  const heading = useArrivalFocus<HTMLHeadingElement>(Boolean(detail.data));
  const started = useRef(false);
  const data = detail.data;
  usePageTitle(
    data ? t('manual.page.title', { id: data.criterion.id, title: data.criterion.title }) : t('manual.title'),
  );

  async function begin(body: Parameters<typeof startReview>[2]) {
    setStartError(null);
    setBusy(true);
    setRecorded(false);
    try {
      const draft = await startReview(siteId, criterion, body);
      await refresh();
      setActiveId(draft.id);
    } catch (failure) {
      const code = errorCode(failure);
      setStartError(code === 'pagina' ? t('manual.errors.pagina') : t('manual.errors.generic'));
    } finally {
      setBusy(false);
    }
  }

  // Coming from a result, a fix being checked or a review to repeat: the review starts from there.
  // biome-ignore lint/correctness/useExhaustiveDependencies: it starts once, from the address
  useEffect(() => {
    if (!data || !rights.track || started.current || search.r) return;
    if (search.p || search.occurrence || search.previous) {
      started.current = true;
      void begin({
        ...(search.p ? { pageKey: search.p } : {}),
        ...(search.occurrence ? { occurrenceId: search.occurrence } : {}),
        ...(search.previous ? { previousId: search.previous } : {}),
      });
    }
  }, [data, rights.track]);

  if (detail.isPending) return <p role="status">{t('app.loading')}</p>;
  if (detail.isError || !data) return <Message tone="error">{t('manual.page.loadFailed')}</Message>;

  const active = data.drafts.find((draft) => draft.id === activeId);
  const guide = data.guide;

  return (
    <section aria-labelledby="comprobacion-manual" className="manual-page">
      <p>
        <Link
          to={search.scan ? '/sites/$siteId/scans/$scanId' : '/sites/$siteId'}
          params={search.scan ? { siteId, scanId: search.scan } : { siteId }}
          onClick={() => search.scan && writeStored(`tab:${search.scan}`, 'manual')}
          className="inline-flex items-center gap-1"
        >
          <ArrowLeftIcon aria-hidden="true" />
          {t('manual.actions.back')}
        </Link>
      </p>
      <header className="manual-head">
        <h2 id="comprobacion-manual" ref={heading} tabIndex={-1} className="focus:outline-none">
          {t('manual.page.title', { id: data.criterion.id, title: data.criterion.title })}
        </h2>
        <p className="problem-chips">
          <span className="origin-chip">{t('manual.page.level', { level: data.criterion.level })}</span>
          <span className="origin-chip">{data.criterion.principleName}</span>
          <HelpBadge help={guide?.help ?? null} />
        </p>
        <p>{data.criterion.summary}</p>
        <p>{t('manual.intro')}</p>
        <p className="results-note">{t('manual.notAudit')}</p>
        {guide && (
          <p className="text-sm text-ink-muted">{t('manual.guide.version', { version: guide.version })}</p>
        )}
      </header>

      <Guide detail={data} />

      {guide && (
        <div className="manual-mine">
          {!rights.track ? (
            <p className="text-sm text-ink-muted">{t('manual.readOnly')}</p>
          ) : (
            <>
              {recorded && (
                <p role="status" className="manual-recorded">
                  <CheckCircledIcon aria-hidden="true" /> {t('manual.saved.registered')}
                </p>
              )}
              {startError && <Message tone="error">{startError}</Message>}
              {active ? (
                <ReviewEditor
                  key={active.id}
                  draft={active}
                  detail={data}
                  siteId={siteId}
                  onRegistered={() => {
                    setActiveId(null);
                    setRecorded(true);
                  }}
                  onDiscarded={() => setActiveId(null)}
                />
              ) : (
                <>
                  {data.drafts.length > 0 && (
                    <section aria-labelledby="borradores" className="manual-drafts">
                      <h3 id="borradores">{t('manual.drafts.title')}</h3>
                      <p className="text-sm text-ink-muted">{t('manual.drafts.intro')}</p>
                      <ul className="follow-list">
                        {data.drafts.map((draft) => (
                          <li key={draft.id} className="follow-item">
                            <p className="font-bold">
                              {draft.page
                                ? draft.page.title || pathOf(draft.page.url)
                                : t('manual.form.whole')}
                              {draft.element ? ` — ${draft.element}` : ''}
                            </p>
                            <p className="text-sm text-ink-muted">
                              {t(`manual.form.devices.${draft.device}`)} ·{' '}
                              {t('manual.drafts.updated', { date: date(draft.updatedAt, 'short') })}
                            </p>
                            <div>
                              <Button variant="secondary" onPress={() => setActiveId(draft.id)}>
                                {t('manual.drafts.open')}
                              </Button>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </section>
                  )}
                  <NewReview detail={data} busy={busy} onStart={(body) => void begin(body)} />
                </>
              )}
            </>
          )}
        </div>
      )}

      <section aria-labelledby="anteriores" className="manual-history">
        <h3 id="anteriores">{t('manual.history.title')}</h3>
        <p className="text-sm text-ink-muted">{t('manual.history.intro')}</p>
        {data.reviews.length === 0 ? (
          <p>{t('manual.history.empty')}</p>
        ) : (
          <ul className="follow-list">
            {data.reviews.map((review) => (
              <ReviewCard
                key={review.id}
                review={review}
                mayTrack={rights.track}
                busy={busy}
                onRepeat={(previous) =>
                  void begin({
                    previousId: previous.id,
                    ...(previous.page ? { pageKey: previous.page.key } : {}),
                  })
                }
              />
            ))}
          </ul>
        )}
      </section>
      <p>
        <Link
          to={search.scan ? '/sites/$siteId/scans/$scanId' : '/sites/$siteId'}
          params={search.scan ? { siteId, scanId: search.scan } : { siteId }}
          onClick={() => search.scan && writeStored(`tab:${search.scan}`, 'manual')}
          className={buttonClass('secondary')}
        >
          {t('manual.actions.back')}
        </Link>
      </p>
    </section>
  );
}

/** Where a review starts: the page, the device and the element. A review of the whole website needs no page. */
function NewReview({
  detail,
  busy,
  onStart,
}: {
  detail: GuideDetail;
  busy: boolean;
  onStart: (body: { pageKey?: string | null; element?: string | null; device?: ReviewDevice }) => void;
}) {
  const { t } = useTranslation();
  const scope = detail.guide?.scope ?? 'page';
  const [pageKey, setPageKey] = useState('');
  const [device, setDevice] = useState<ReviewDevice>('ambos');
  const [element, setElement] = useState('');
  if (scope === 'page' && detail.pages.length === 0) return <p>{t('manual.page.noPages')}</p>;
  return (
    <Form
      className="manual-form"
      validationBehavior="native"
      onSubmit={(event) => {
        event.preventDefault();
        onStart({
          ...(scope === 'page' ? { pageKey } : {}),
          element: element.trim() || null,
          device,
        });
      }}
    >
      <h3>{t('manual.drafts.start')}</h3>
      {scope === 'page' ? (
        <div className="flex flex-col gap-2">
          <label htmlFor="nueva-pagina" className="font-bold">
            {t('manual.form.page')}
          </label>
          <select
            id="nueva-pagina"
            className={SELECT_CLASS}
            value={pageKey}
            required
            onChange={(event) => setPageKey(event.target.value)}
          >
            <option value="">{t('manual.form.pagePlaceholder')}</option>
            {detail.pages.map((page) => (
              <option key={page.key} value={page.key}>
                {page.title ? `${page.title} (${pathOf(page.url)})` : pathOf(page.url)}
              </option>
            ))}
          </select>
        </div>
      ) : (
        <p>{t('manual.page.siteScope')}</p>
      )}
      <Field
        name="element"
        label={t('manual.form.element')}
        description={t('manual.form.elementHelp')}
        value={element}
        onChange={setElement}
        maxLength={200}
      />
      <div className="flex flex-col gap-2" role="radiogroup" aria-labelledby="nueva-dispositivo">
        <span id="nueva-dispositivo" className="font-bold">
          {t('manual.form.device')}
        </span>
        {(['ambos', 'escritorio', 'movil'] as const).map((value) => (
          <label key={value} className="follow-check">
            <input
              type="radio"
              name="nueva-dispositivo"
              value={value}
              checked={device === value}
              onChange={() => setDevice(value)}
            />
            {t(`manual.form.devices.${value}`)}
          </label>
        ))}
      </div>
      <div>
        <Button type="submit" isPending={busy}>
          {t('manual.actions.start')}
        </Button>
      </div>
    </Form>
  );
}
